'use client';

import { Typography } from '@musinsa/mds';
import { twMerge } from 'tailwind-merge';
import { useCuratorSnackbar } from '../../../../common/hooks/useCuratorSnackbar';
import type {
  SeedingLabelVariant,
  SeedingShippingBanner,
  SeedingShippingDetailRow,
} from '../types';
import { StatusLabelBadge } from './StatusLabelBadge';

/**
 * 복사 토스트 주어 — 라벨별로 조사(이/가)까지 결합해 둔다.
 * "{주어} 복사되었습니다." 형태라 라벨에 따라 받침 유무가 달라지는 '이(가)' 표기를 피한다.
 */
const COPY_TOAST_SUBJECT: Record<string, string> = {
  '송장 번호': '송장 번호가',
};

interface ShippingTrackingCardProps {
  /** 카드 타이틀 (default = "배송 조회") */
  title?: string;
  /** Label 텍스트 (예: "배송중", "콘텐츠 등록 완료"). null 이면 배지 미노출 */
  statusLabel: string | null;
  /** Label 색상 variant */
  labelVariant: SeedingLabelVariant;
  /** 안내 배너 (1~2개) */
  banners: SeedingShippingBanner[];
  /** 택배사/운송장/일자 — null 이면 details 영역 미노출 (준비중 단계) */
  details: SeedingShippingDetailRow[] | null;
  className?: string;
}

export const ShippingTrackingCard = ({
  title = '배송 조회',
  statusLabel,
  labelVariant,
  banners,
  details,
  className,
}: ShippingTrackingCardProps) => {
  const snackbar = useCuratorSnackbar();

  const handleCopy = async (value: string, fieldLabel: string) => {
    try {
      await navigator.clipboard.writeText(value);
      // 라벨별 조사까지 결합한 주어를 미리 정의해 '이(가)' 분기를 없앤다. (피드백)
      const subject = COPY_TOAST_SUBJECT[fieldLabel] ?? `${fieldLabel}이(가)`;
      snackbar.show({ message: `${subject} 복사되었습니다.` });
    } catch {
      snackbar.show({ message: '복사에 실패했습니다.' });
    }
  };

  return (
    <section className={twMerge('px-[16px]!', className)}>
      <div className="pt-[12px]! pb-[11px]! flex items-center gap-[8px]">
        <Typography variant="title18pxMed" colorScheme="black">
          {title}
        </Typography>
        {statusLabel && <StatusLabelBadge variant={labelVariant} label={statusLabel} />}
      </div>

      {banners.length > 0 && (
        <div className="flex flex-col gap-[8px] mb-[12px]!">
          {banners.map((banner) => (
            <div key={banner.title} className="rounded-[4px] bg-gray-100 px-[12px]! py-[10px]!">
              <Typography variant="body13pxSemi" colorScheme="black">
                {banner.title}
              </Typography>
              {banner.subtitle && (
                <Typography
                  variant="body13pxReg"
                  colorScheme="gray-600"
                  className="mt-[2px]! block"
                >
                  {banner.subtitle}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}

      {details && details.length > 0 && (
        <div className="flex flex-col gap-[8px]">
          {details.map((row) => (
            <div key={row.label} className="flex justify-between items-center py-[4px]!">
              <Typography variant="body13pxReg" colorScheme="gray-600">
                {row.label}
              </Typography>
              {row.copyable ? (
                <button
                  type="button"
                  className="flex items-center gap-[4px]"
                  onClick={() => handleCopy(row.value, row.label)}
                >
                  <Typography variant="body13pxReg" colorScheme="black">
                    {row.value}
                  </Typography>
                  <CopyIcon />
                </button>
              ) : (
                <Typography variant="body13pxReg" colorScheme="black">
                  {row.value}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>복사</title>
    <path
      d="M10.667 1.333H3.333A1.333 1.333 0 002 2.667v9.333h1.333V2.667h7.334V1.333zm2 2.667H6A1.333 1.333 0 004.667 5.333v9.334A1.333 1.333 0 006 16h6.667a1.333 1.333 0 001.333-1.333V5.333A1.333 1.333 0 0012.667 4zM6 14.667V5.333h6.667v9.334H6z"
      fill="#8A8A8A"
    />
  </svg>
);
